import { Container, Graphics, Text } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette } from '../render/Palette';
import { ARTIFACTS_BY_ID } from '../sim/artifacts/ArtifactDefs';
import { CURSES_BY_ID } from '../sim/artifacts/CurseDefs';
import type { RunState } from '../sim/run/RunState';

const PANEL_W = 520;
const ROW_H = 44;

interface CleanseRow {
  label: string;
  sub: string;
  run: () => void;
}

/**
 * Cleansing altar picker (vault §4.2): choose ONE curse to remove — either a
 * cursed equipped artifact (keeps the boon and quality, drops the drawback)
 * or a shrine curse taken this run. Confirming spends the altar; cancelling
 * leaves it for another attempt.
 */
export class CleanseOverlay implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private focus = 0;
  private rows: CleanseRow[] = [];
  private rowY: number[] = [];
  private focusRing = new Graphics();
  private acceptDelay = 8;
  private panelX: number;

  constructor(
    private readonly ctx: GameContext,
    runState: RunState,
    cleanseArtifact: (artifactId: string, quality: number) => void,
    private readonly onCleansed: () => void,
  ) {
    for (const a of runState.artifacts) {
      if (a.proc !== 'cursed' || a.curseId === null) continue;
      const def = ARTIFACTS_BY_ID.get(a.artifactId);
      const curse = CURSES_BY_ID.get(a.curseId);
      this.rows.push({
        label: `${def?.name ?? a.artifactId}  Q ${Math.round(a.quality * 100)}%`,
        sub: `${curse?.name ?? a.curseId}: ${curse?.desc ?? ''}`,
        run: () => cleanseArtifact(a.artifactId, a.quality),
      });
    }
    runState.shrineCurses.forEach((entry, i) => {
      const curse = CURSES_BY_ID.get(entry.curseId);
      this.rows.push({
        label: `Shrine curse — ${curse?.name ?? entry.curseId}`,
        sub: curse?.desc ?? '',
        run: () => {
          runState.stats.removeSource(entry.source);
          runState.shrineCurses.splice(i, 1);
        },
      });
    });

    const w = ctx.app.screen.width;
    const h = ctx.app.screen.height;
    const panelH = 130 + this.rows.length * ROW_H;
    this.panelX = (w - PANEL_W) / 2;
    const panelY = (h - panelH) / 2;

    this.root.addChild(new Graphics().rect(0, 0, w, h).fill({ color: 0x05060c, alpha: 0.75 }));
    this.root.addChild(new Graphics()
      .roundRect(this.panelX, panelY, PANEL_W, panelH, 10)
      .fill(Palette.hudPanel)
      .stroke({ width: 2, color: Palette.playerCool, alpha: 0.7 }));

    const title = new Text({
      text: 'CLEANSING ALTAR',
      style: {
        fontFamily: 'monospace', fontSize: 22, fontWeight: 'bold',
        fill: Palette.playerCool, letterSpacing: 3,
      },
    });
    title.anchor.set(0.5, 0);
    title.position.set(w / 2, panelY + 18);
    this.root.addChild(title);

    const sub = new Text({
      text: 'choose one curse to lift — the boon remains',
      style: { fontFamily: 'monospace', fontSize: 12, fill: Palette.hudDim },
    });
    sub.anchor.set(0.5, 0);
    sub.position.set(w / 2, panelY + 48);
    this.root.addChild(sub);

    this.root.addChild(this.focusRing);
    this.rows.forEach((row, i) => {
      const y = panelY + 78 + i * ROW_H;
      const text = new Text({
        text: `${row.label}\n${row.sub}`,
        style: { fontFamily: 'monospace', fontSize: 12, fill: Palette.hudText, lineHeight: 16 },
      });
      text.position.set(this.panelX + 24, y);
      this.rowY.push(y);
      this.root.addChild(text);
    });

    const hint = new Text({
      text: 'cleanse  [E / Ⓐ]      leave  [Esc / Ⓑ]',
      style: { fontFamily: 'monospace', fontSize: 12, fill: Palette.hudDim },
    });
    hint.anchor.set(0.5);
    hint.position.set(w / 2, panelY + panelH - 18);
    this.root.addChild(hint);
  }

  enter(): void {}
  exit(): void {}

  update(_dt: number, input: InputSnapshot): void {
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }
    if (input.navY !== 0 && this.rows.length > 0) {
      this.focus = (this.focus + input.navY + this.rows.length) % this.rows.length;
    }
    if (input.confirmPressed) {
      const row = this.rows[this.focus];
      if (row) {
        row.run();
        this.onCleansed();
      }
      this.ctx.scenes.pop();
      return;
    }
    if (input.cancelPressed || input.pausePressed) this.ctx.scenes.pop();
  }

  render(_alpha: number): void {
    const g = this.focusRing;
    g.clear();
    const y = this.rowY[this.focus];
    if (y === undefined) return;
    g.roundRect(this.panelX + 16, y - 6, PANEL_W - 32, ROW_H - 4, 6)
      .stroke({ width: 2, color: Palette.focus });
  }
}
